import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { Lightbulb, AlertTriangle, CheckCircle2 } from "lucide-react";

const Cleaning = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Cleaning Essentials" 
        description="Brushing, skimming, and vacuuming techniques for pristine pools"
      >
        <section className="space-y-8">
          {/* Why Cleaning Matters */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">Why Physical Cleaning Matters</h2>
            <p className="text-muted-foreground">
              Chemicals can't do everything. Organic debris consumes chlorine. Biofilm on surfaces protects 
              algae from treatment. Stains become permanent if left too long. Regular physical cleaning works 
              with your chemistry — not instead of it.
            </p>
          </div>

          {/* Skimming */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Surface Skimming</h2>
            <p className="text-muted-foreground mb-4">
              Always start with skimming. Removing debris from the surface before it sinks prevents 
              staining and reduces the load on your filter and chemistry.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Technique</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Start at the far end from the skimmer, work toward it</li>
                <li>• Use long, smooth strokes — don't stir up debris</li>
                <li>• Get into corners and behind ladders</li>
                <li>• Clear the skimmer basket before you start so debris flows in</li>
              </ul>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">San Diego Note:</strong> Santa Ana wind days can drop 
                  significant debris in hours. Plan extra skimming time during these conditions.
                </p>
              </div>
            </div>
          </div>

          {/* Brushing */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Brushing</h2>
            <p className="text-muted-foreground mb-4">
              Brushing is the most underrated cleaning task. It breaks up biofilm, dislodges algae before 
              it anchors, and helps chemicals reach surfaces. Skip it and you're inviting problems.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Priority Areas</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Steps and benches — rough surfaces trap algae</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Behind ladders and handrails — poor circulation zones</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Corners — dead spots with minimal water movement</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Waterline tile — calcium and body oils accumulate</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Around light niches — often missed</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Spillover ledges and raised spa walls</li>
              </ul>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Brush Types</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• <strong>Nylon:</strong> For vinyl, fiberglass, painted surfaces</li>
                  <li>• <strong>Stainless steel:</strong> For unpainted concrete/plaster</li>
                  <li>• <strong>Combo:</strong> Nylon with steel corners for tile work</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Technique</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Push debris toward main drain</li>
                  <li>• Overlap strokes slightly</li>
                  <li>• Apply firm, consistent pressure</li>
                  <li>• Work top to bottom on walls</li>
                </ul>
              </div>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> If you see early algae spots (before they 
                  bloom), aggressive brushing combined with elevated chlorine can stop it without a full shock treatment.
                </p>
              </div>
            </div>
          </div>

          {/* Vacuuming */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Vacuuming</h2>
            <p className="text-muted-foreground mb-4">
              Vacuuming removes settled debris from the floor that skimming and brushing can't reach.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Manual Vacuuming</h4>
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li>Attach vacuum head to pole, connect hose to vacuum head</li>
                <li>Submerge the hose to fill with water (no air bubbles)</li>
                <li>Connect the other end to skimmer suction port</li>
                <li>Work slowly in overlapping passes — fast movement stirs debris</li>
                <li>Check skimmer basket periodically for debris buildup</li>
              </ol>
            </div>
            <div className="bg-card border rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-foreground mb-3">When to Vacuum to Waste</h4>
              <p className="text-sm text-muted-foreground mb-2">
                For heavy debris, algae, or fine particles that would clog the filter, set the multiport 
                valve to "Waste" to bypass the filter entirely. Use for:
              </p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Dead algae after treatment</li>
                <li>• Heavy dirt or mud</li>
                <li>• Construction debris</li>
                <li>• Anything you don't want in the filter</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2 font-medium">
                Note: This removes water from the pool. Monitor level and add water as needed.
              </p>
            </div>
          </div>

          {/* Robotic Cleaners */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Robotic Cleaners</h2>
            <p className="text-muted-foreground mb-4">
              Many BlueLux clients have robotic cleaners. These supplement but don't replace your work.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Your Responsibilities</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Empty the filter basket/canister when you service</li>
                <li>• Check for tangled cord or stuck position</li>
                <li>• Inspect brushes and wheels for wear</li>
                <li>• Verify it's running correctly during your visit</li>
                <li>• Note any issues in the app</li>
              </ul>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> Robots clean floors and walls but don't 
                  brush like you can. Still brush steps, corners, and behind ladders manually.
                </p>
              </div>
            </div>
          </div>

          {/* Tile Line */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Tile Line Cleaning</h2>
            <p className="text-muted-foreground mb-4">
              The waterline tile collects body oils, sunscreen, and calcium scale. Regular attention 
              prevents stubborn buildup that requires professional cleaning.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <ul className="space-y-2 text-muted-foreground">
                <li>• Brush tile line weekly during regular service</li>
                <li>• Use a pumice stone for calcium scale (on tile only, not plaster)</li>
                <li>• Tile cleaner spray for stubborn oils and scum</li>
                <li>• For heavy scale, note for client — may need professional treatment</li>
              </ul>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">San Diego Note:</strong> Our hard water accelerates calcium 
                  buildup at the waterline. Regular attention prevents it from becoming a major project.
                </p>
              </div>
            </div>
          </div>

          {/* Service Sequence */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Recommended Cleaning Sequence</h2>
            <div className="bg-card border rounded-lg p-6">
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <span><strong className="text-foreground">Skim surface</strong> — remove floating debris first</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <span><strong className="text-foreground">Brush walls and steps</strong> — push debris toward floor</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <span><strong className="text-foreground">Brush tile line</strong> — prevent calcium buildup</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <span><strong className="text-foreground">Vacuum floor</strong> — collect everything you've loosened</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <span><strong className="text-foreground">Empty baskets</strong> — skimmer and pump</span>
                </li>
              </ol>
            </div>
          </div>

          {/* Why It Matters */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-3">Why This Matters</h3>
            <p className="text-muted-foreground">
              Premium clients expect their pools to look perfect. Physical cleaning is the most visible 
              part of what we do. A chemically balanced pool with debris floating, algae in corners, or 
              dirty tile line doesn't look cared for — no matter how good the test results are.
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Cleaning;